import WithUserTimeHOC, { WithUserTimeProps } from '@hocs/WithUserTime'
import { GET_GROUP_PROFILE } from '@components/Modal/GroupProfileModal/QueryGroupProfile'
import { GET_DATA as GET_STUDENT_DATA } from '@components/Modal/SingleProfileModal/QuerySingleProfile'
import { TProfile } from '@interfaces/MyLesson'
import {
  StudentLanguageProfileInfo as StudentLanguageProfileInfoInterface,
  StudentProfileInfo as StudentProfileInfoInterface,
} from '@interfaces/Student'
import { GET_LEARNING_PROFILES } from '@queries/index'
import { lessonExpectations } from '@utils/stubdata/settings.json'
import find from 'lodash/find'
import includes from 'lodash/includes'
import isBoolean from 'lodash/isBoolean'
import get from 'lodash/get'
import map from 'lodash/map'
import React from 'react'
import { withApollo, WithApolloClient } from 'react-apollo'

interface GroupInfo {
  Id: string
  StudentName: string
  PurposeOfStudy: string
}

interface QueryPageResults {
  loading: boolean
  error: boolean
  refetch: Function
  Profiles: TProfile | TProfile[]
}

interface QueryPageDataProp {
  ProgramType: string
  StudentGroupId: string
  Language: string
  children: (results: QueryPageResults) => {}
}

export type Props = WithApolloClient<QueryPageDataProp & WithUserTimeProps>

class QueryProfilesData extends React.Component<Props> {
  _isMounted = false
  abortCtrl = new AbortController()

  state = {
    loading: false,
    error: false,
    Profiles: [] as TProfile | TProfile[],
  }

  async componentDidMount() {
    this._isMounted = true
    this.fetchData()
  }

  componentWillUnmount() {
    this._isMounted = false
    this.abortCtrl.abort()
  }

  fetchData = async () => {
    try {
      this.setState({
        loading: true,
        error: false,
      })

      const Profiles = await this.getProfile()

      if (this._isMounted) {
        this.setState({
          loading: false,
          Profiles,
        })
      }
    } catch (error) {
      if (this._isMounted) {
        this.setState({
          loading: false,
          error: true,
        })
      }
    }
  }

  getProfile = async () => {
    const { ProgramType } = this.props
    if (ProgramType === 'Individual') {
      return this.getIndividualProfile()
    }
    return this.getGroupProfile()
  }

  getIndividualProfile = async (): Promise<TProfile> => {
    const { client, StudentGroupId, Language } = this.props
    const student = await client.query({
      query: GET_STUDENT_DATA,
      variables: { StudentId: StudentGroupId, Language },
      fetchPolicy: 'network-only',
    })

    const StudentProfileInfo: StudentProfileInfoInterface = get(student.data, 'getStudents[0].StudentProfileInfo', {})
    const lang = StudentProfileInfo.PortalLanguage || Language
    const profileQuery = await client.query({
      query: GET_LEARNING_PROFILES,
      variables: { StudentId: StudentGroupId, Language },
      fetchPolicy: 'network-only',
    })

    const profiles: StudentLanguageProfileInfoInterface[] = get(profileQuery.data, 'getLearningProfiles', []).map(
      ({ StudentLanguageProfileInfo }) => StudentLanguageProfileInfo
    )

    const profile = find(profiles, { Language: lang })

    const profileInfoIds = map(profile, (value, key) => isBoolean(value) && value && key).filter(id => id)

    const LessonExpectations = lessonExpectations
      .map(({ id, label }) => (includes(profileInfoIds, id) && label) || '')
      .filter(label => label)

    return {
      Purpose: get(profile, 'Purpose', ''),
      Expectations: LessonExpectations,
    }
  }

  getGroupProfile = async (): Promise<TProfile[]> => {
    const { client, StudentGroupId } = this.props

    const profile = await client.query({
      query: GET_GROUP_PROFILE,
      variables: { StudentGroupId },
      fetchPolicy: 'network-only',
    })

    const ProfileInfo: GroupInfo[] = get(profile.data, 'getGroupProfile.StudentGroupInfo.StudentInfos', [])

    return map(ProfileInfo, profileInfo => ({
      ...profileInfo,
      Purpose: profileInfo.PurposeOfStudy,
      Expectations: [],
    }))
  }

  render() {
    const { children } = this.props
    const { loading, error, Profiles } = this.state

    return children({
      loading,
      error,
      Profiles,
      refetch: this.fetchData,
    })
  }
}

export default WithUserTimeHOC(withApollo(QueryProfilesData))
